import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import React, { useEffect, useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import {
  orderCancel,
  orderDelete,
  orderDetails,
  orderList,
  orderOrdering,
  orderStatusChange,
} from "../../../../services/orderService";
import ManageOrderDrawer from "../../../../components/sections/front/Order/Drawer/ManageOrderDrawer";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { eventList } from "../../../../services/eventService";
import Select from "../../../../components/form/select/Select";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useSelector } from "react-redux";
import Modal from "../../../../components/elements/Modal";
import Form from "../../../../components/form/Form";
import useForm from "../../../../hooks/useForm";
import { classNames } from "../../../../helpers/classNames";
import Radio from "../../../../components/form/Radio";
import Errors from "../../../../components/elements/Errors";
import Input from "../../../../components/form/Input";
import stripe from "../../../../assets/images/stripe.svg";
import paypal from "../../../../assets/images/paypal.svg";
import Image from "../../../../components/elements/Image";
import Checkbox from "../../../../components/form/Checkbox";
import Skeleton from "../../../../components/loader/Skeleton";
import { useTranslation } from "react-i18next";

const initial_validation = {
  refundType: { required: true, message: "Refund type is required" },
  // cancelReason: { required: true, message: "this field is required" },
  // receiveAmount: { required: true, message: "this field is required" },
  creditAmountType: { required: true, message: "Credit amount is required" },
};

let validation = initial_validation;

const statusList = [
  { name: "processing", value: "processing", type: "checkbox" },
  // { name: "shipped", value: "shipped", type: "checkbox" },
  // { name: "pickup", value: "pickup", type: "checkbox" },
  // { name: "delivered", value: "delivered", type: "checkbox" },
  { name: "canceled", value: "canceled", type: "checkbox" },
  { name: "confirmed", value: "confirmed", type: "checkbox" },
  // { name: "return-request", value: "return-request", type: "checkbox" },
  { name: "cancel-request", value: "cancel-request", type: "checkbox" },
  { name: "partial-refund", value: "partial-refund", type: "checkbox" },
  // { name: "rejected", value: "rejected", type: "checkbox" },
];

// Payment status options for filtering
const paymentStatusList = [
  { name: "pending", value: "pending", type: "checkbox" },
  { name: "Success", value: "success", type: "checkbox" },
  { name: "failed", value: "failed", type: "checkbox" },
];

// Status options for order management
const ORDER_STATUS_OPTIONS = [
  { name: "Processing", value: "processing" },
  { name: "Confirmed", value: "confirmed" },
  // { name: "Shipped", value: "shipped" },
  // { name: "Pickup", value: "pickup" },
  // { name: "Delivered", value: "delivered" },
  { name: "Canceled", value: "canceled" },
  // { name: "Return Request", value: "return-request" },
  { name: "Cancel Request", value: "cancel-request" },
  { name: "Partial Refund", value: "partial-refund" },
  // { name: "Rejected", value: "rejected" },
];

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const statusStyles = {
    processing: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    shipped: {
      backgroundColor: "#DBEAFE", // Blue background
      color: "#1D4ED8", // Dark blue text
      borderColor: "#3B82F6", // Blue border
    },
    pickup: {
      backgroundColor: "#FCE7F3", // Pink background
      color: "#BE185D", // Dark pink text
      borderColor: "#EC4899", // Pink border
    },
    delivered: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    confirmed: {
      backgroundColor: "#ECFDF5", // Light emerald background - Fresh confirmation color
      color: "#065F46", // Dark emerald text - Strong contrast
      borderColor: "#059669", // Emerald border - Professional accent
    },
    canceled: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
    "return-request": {
      backgroundColor: "#FDF4FF", // Purple background
      color: "#7C2D92", // Dark purple text
      borderColor: "#A855F7", // Purple border
    },
    "cancel-request": {
      backgroundColor: "#FDF4FF", // Purple background
      color: "#7C2D92", // Dark purple text
      borderColor: "#A855F7", // Purple border
    },
    "partial-refund": {
      backgroundColor: "#F3F4F6", // Gray background
      color: "#374151", // Dark gray text
      borderColor: "#6B7280", // Gray border
    },
    rejected: {
      backgroundColor: "#F3F4F6", // Gray background
      color: "#374151", // Dark gray text
      borderColor: "#6B7280", // Gray border
    },
  };

  return (
    statusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Payment Status Styling Function
const getPaymentStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const paymentStatusStyles = {
    pending: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    success: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    failed: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
  };

  return (
    paymentStatusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Order Status Update Modal Component
const OrderStatusUpdateModal = ({
  isOpen,
  onClose,
  orderItemData,
  onSuccess,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [percentageAmount, setPercentageAmount] = useState(0);
  const [openPayment, setOpenPayment] = useState(false);
  const [receiveAmount, setReceiveAmount] = useState(0);
  const queryClient = useQueryClient();

  const { data: orderData, isLoading } = useQuery({
    queryKey: ["details", orderItemData?._id],
    queryFn: async () => {
      const res = await orderDetails({ id: orderItemData?._id });
      return {
        ...res,
        data: {
          ...res?.data,
          orderItemData: res?.data?.orderItemData?.map((item) => ({
            ...item,
            remainingRefundableAmount:
              (item?.finalPrice || 0) - (item?.refund?.refundAmount || 0),
          })),
        },
      };
    },
    enabled: !!orderItemData?._id,
  });

  const {
    values,
    handleChange,
    handleSubmit: handleFormSubmit,
    errors,
    setFieldsValue,
    formValidate,
    resetField,
  } = useForm({
    validationRules: validation,
  });

  // Define status hierarchy and allowed transitions
  const statusHierarchy = {
    processing: 0,
    confirmed: 1,
    shipped: 2,
    pickup: 3,
    delivered: 4,
    "cancel-request": 1,
    canceled: 5,
    "return-request": 4,
    "partial-refund": 4,
    rejected: 5,
  };

  useEffect(() => {
    if (values?.creditAmountType === "full") {
      const refundArray = Array.isArray(orderData?.data?.refund)
        ? orderData?.data?.refund
        : [];
      const already_refund_amount =
        refundArray?.reduce(
          (acc, curent) => (acc || 0) + (curent?.refundAmount || 0),
          0
        ) || 0;
      const total_receive_amount =
        (values?.refundType === "full"
          ? orderData?.data?.total - already_refund_amount
          : 0) -
        (values?.keepTransactionFee
          ? orderData?.data?.organizationTransactionFee
          : 0);

      const total_remaining_amount = values?.keepTransactionFee
        ? orderData?.data?.organizationTransactionFee
        : 0;

      setReceiveAmount(total_receive_amount);
      setRemaining(total_remaining_amount);
      return;
    }
    if (
      values?.creditAmountType === "percentage" ||
      values?.creditAmountType === "specific"
    ) {
      const total_remaining_amount = orderData?.data?.total || 0;
      setRemaining(total_remaining_amount);
      return;
    }
  }, [values]);

  // Define which statuses are final (cannot be changed)
  const finalStatuses = ["delivered", "canceled", "partial-refund", "rejected"];

  // Define which statuses allow only forward movement
  const forwardOnlyStatuses = ["confirmed", "shipped", "pickup"];

  // Get allowed status options based on current status
  const getAllowedStatusOptions = (currentStatus) => {
    if (!currentStatus) return ORDER_STATUS_OPTIONS;

    const currentLevel = statusHierarchy[currentStatus];

    // If current status is final, no changes allowed
    if (finalStatuses.includes(currentStatus)) {
      return [];
    }

    // Filter options based on business rules
    return ORDER_STATUS_OPTIONS.filter((option) => {
      const optionLevel = statusHierarchy[option.value];

      // Special cases for cancel and return requests
      if (currentStatus === "cancel-request") {
        return ["canceled", "confirmed", "shipped"].includes(option.value);
      }

      if (currentStatus === "return-request") {
        return ["partial-refund", "rejected", "delivered"].includes(
          option.value
        );
      }

      // For forward-only statuses, only allow progression or lateral moves
      if (forwardOnlyStatuses.includes(currentStatus)) {
        // Allow same level or higher level statuses
        if (optionLevel >= currentLevel) return true;

        // Allow cancel/return requests from any forward status
        if (["cancel-request", "return-request"].includes(option.value))
          return true;

        return false;
      }

      // For processing, allow any status
      if (currentStatus === "processing") {
        return true;
      }

      return true;
    });
  };

  const allowedOptions = getAllowedStatusOptions(orderData?.data?.status?.info);

  // Initialize status when modal opens
  React.useEffect(() => {
    if (isOpen && orderData?.data) {
      setSelectedStatus(orderData?.data?.status?.info || "processing");
      setOpenPayment(false);
    }
  }, [isOpen, orderData?.data]);

  // Combined status update and cancellation mutation
  const { mutate: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: async () => {
      // If changing to canceled status, handle the cancellation/refund logic
      if (selectedStatus === "canceled" && openPayment) {
        if (orderData?.data?.status?.info !== "processing") {
          if (values?.refundType === "full") {
            const cancelPayload = {
              id: orderData?.data?._id,
              cancelReason: values?.cancelReason,
              amount: Number(receiveAmount),
              refundType: values?.refundType,
              refund: {
                status: "requested",
                info: {
                  refundType: values?.refundType,
                  type: values?.creditAmountType,
                  value: Number(percentageAmount),
                  remainingAmount: Number(remaining),
                },
              },
            };
            const res = await orderCancel(cancelPayload);
            return { res };
          }
          if (values?.refundType === "partial") {
            const cancelPayload = {
              id: orderData?.data?._id,
              refundType: values?.refundType,
              orderItemsData: Object.keys(values?.partialData || {})?.map(
                (key) => ({
                  orderItemId: key,
                  cancelReason: values?.partialData?.[key]?.cancelReason,
                  amount: Number(
                    values?.partialData?.[key]?.receiveAmount || 0
                  ),
                  refundType: values?.refundType,
                  refund: {
                    status: "requested",
                    info: {
                      refundType: values?.refundType,
                      type: values?.partialData?.[key]?.creditAmountType,
                      value: Number(
                        values?.partialData?.[key]?.givenAmount || 0
                      ),
                      remainingAmount: Number(
                        values?.partialData?.[key]?.remainingAmount || 0
                      ),
                    },
                  },
                })
              ),
            };
            const res = await orderCancel(cancelPayload);
            return { res };
          }
        } else {
          const res = await orderStatusChange({
            ids: [orderData?.data?._id],
            status: "canceled",
          });
          return { res, refundType: values?.refundType };
        }
      } else {
        // Regular status change
        const res = await orderStatusChange({
          ids: [orderData?.data._id],
          status: selectedStatus,
        });
        return { res };
      }
    },
    onSuccess: ({ res, refundType }) => {
      if (res?.status === 200) {
        if (selectedStatus === "canceled" && openPayment) {
          queryClient.invalidateQueries({
            queryKey: ["details", orderData?.data?._id],
          });
          resetField();
          setFieldsValue({ refundType, cancelReason: "" });
          setPercentageAmount(0);
          setRemaining(0);
          setReceiveAmount(0);
        }
        onSuccess(res);
        onClose();
        toast.success("Status updated successfully!");
      } else {
        toast.warning(res?.message || "Failed to update status");
      }
    },
    onError: (error) => {
      console.error("Status update error:", error);
      toast.error(error?.message || "Failed to update order status");
    },
  });

  const handleSubmit = () => {
    if (!selectedStatus) {
      toast.warning("Please select a status");
      return;
    }

    // Additional validation
    const currentStatus = orderData?.data?.status?.info;
    if (finalStatuses.includes(currentStatus)) {
      toast.warning(
        "This order status cannot be changed as it's already finalized."
      );
      return;
    }

    // If canceling with payment options, validate the form
    if (selectedStatus === "canceled" && openPayment) {
      // Manually validate form fields for cancellation
      if (!values?.refundType) {
        toast.warning("Please select a refund option");
        return;
      }

      if (values?.refundType === "full" && !values?.creditAmountType) {
        toast.warning("Please select a credit amount type");
        return;
      }

      updateStatus();
    } else {
      updateStatus();
    }
  };

  const handleClose = () => {
    setSelectedStatus("");
    resetField();
    setPercentageAmount(0);
    setRemaining(0);
    setReceiveAmount(0);
    setOpenPayment(false);
    onClose();
  };

  const currentStatusStyle = getStatusStyle(orderData?.data?.status?.info);
  const currentStatus = orderData?.data?.status?.info;
  const isStatusFinal = finalStatuses.includes(currentStatus);

  // Get status change warning message
  const getStatusWarningMessage = (currentStatus) => {
    if (finalStatuses.includes(currentStatus)) {
      return "This order has reached a final status and cannot be modified.";
    }

    if (forwardOnlyStatuses.includes(currentStatus)) {
      return "This order can only move forward in the process or be canceled or returned.";
    }

    return null;
  };

  const warningMessage = getStatusWarningMessage(currentStatus);

  const cancelOptions = [
    {
      value: "full",
      heading: "Cancel entire order",
      subHeading: "Cancel all orders, and choose refund options",
    },
    {
      value: "partial",
      heading: "Refund line item(s)",
      subHeading: "Cancel single order, and choose refund options",
      ...(orderData?.data?.status?.info === "processing" && { disabled: true }),
    },
  ];

  const tableHeadData = [
    {
      _id: "barcode",
      name: "Barcode",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "ticket",
      name: "Ticket",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "totalprice",
      name: "Total Price",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "creditAmountType",
      name: "Refund or credit amount",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "remaining",
      name: "remaining amount",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
  ];

  const tableData = orderData?.data?.orderItemData?.map((item) => {
    const item_value = values?.partialData?.[item?._id];

    return {
      _id: item?._id,
      rowData: [
        {
          _id: "barcode",
          type: "text",
          data: (
            <div>
              {item?.ticketInfo?.uid}
              <div className="text-xs text-slate-400">
                {item?.ticketInfo?.status}
              </div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "ticket",
          type: "text",
          data: (
            <div>
              <div className="max-h-40 overflow-y-auto">
                <p className="text-deep-purple text-sm ">
                  {lngDetect(item?.ticketInfo?.name)}
                </p>
              </div>
              <div className="text-xs text-slate-400">eTicket</div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "price",
          type: "text",
          data: <div>${parseFloat(item?.finalPrice).toFixed(2)}</div>,
          tdClasses: "px-6 py-4",
        },
        {
          _id: "creditAmountType",
          type: "text",
          data: (
            <div>
              {item?.ticketInfo?.paymentType !== "free" ? (
                <>
                  {item?.remainingRefundableAmount > 0 && (
                    <>
                      <Select
                        value={
                          values?.partialData?.[item?._id]?.creditAmountType
                        }
                        placeholder={"Select Option"}
                        dropdownData={[
                          {
                            name: "Full amount",
                            value: "full",
                          },
                          {
                            name: "A percentage of amount",
                            value: "percentage",
                          },
                          {
                            name: "A specific amount",
                            value: "specific",
                          },
                        ]}
                        dropdownButtonClass={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !rounded-md border-slate-300"
                        }
                        onChange={(val) => {
                          if (val) {
                            if (val === "full") {
                              setFieldsValue({
                                partialData: {
                                  ...values?.partialData,
                                  [item?._id]: {
                                    creditAmountType: val,
                                    receiveAmount:
                                      item?.remainingRefundableAmount ||
                                      item?.finalPrice ||
                                      0,
                                    remainingAmount: 0,
                                  },
                                },
                              });
                            } else {
                              setFieldsValue({
                                partialData: {
                                  ...values?.partialData,
                                  [item?._id]: {
                                    creditAmountType: val,
                                    receiveAmount: 0,
                                    remainingAmount: 0,
                                  },
                                },
                              });
                            }
                          } else {
                            const partialData = { ...values?.partialData };
                            delete partialData?.[item?._id];

                            setFieldsValue({ partialData });
                          }
                        }}
                      />

                      {values?.partialData?.[item?._id]?.creditAmountType ===
                        "percentage" && (
                        <div className="w-40 flex items-center gap-1">
                          <Input
                            value={
                              values?.partialData?.[item?._id]?.givenAmount || 0
                            }
                            inputPlaceholder="percentage"
                            onChange={(e) => {
                              let val = Number(e.target.value || 0);

                              if (!isNaN(val)) {
                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      ...values?.partialData?.[item?._id],
                                      percentageValue: val > 100 ? 100 : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                    },
                                  },
                                });
                                if (val > 100) val = 100;
                                if (val < 0) val = 0;

                                const total_remaining_amount =
                                  item?.remainingRefundableAmount || 0;
                                // Calculate percentage of total
                                const amount =
                                  (total_remaining_amount * val) / 100;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      givenAmount: val > 100 ? 100 : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                      receiveAmount: amount,
                                      remainingAmount:
                                        total_remaining_amount - amount,
                                    },
                                  },
                                });
                              }
                            }}
                          />
                          <span>%</span>
                        </div>
                      )}

                      {values?.partialData?.[item?._id]?.creditAmountType ===
                        "specific" && (
                        <div className="w-40 flex items-center gap-1">
                          <span>$</span>
                          <Input
                            value={
                              values?.partialData?.[item?._id]?.givenAmount || 0
                            }
                            inputPlaceholder="amount"
                            onChange={(e) => {
                              let val = Number(e.target.value || 0);
                              if (!isNaN(val)) {
                                const total_remaining_amount =
                                  item?.remainingRefundableAmount || 0;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      ...values?.partialData?.[item?._id],
                                      specificValue:
                                        val > total_remaining_amount
                                          ? total_remaining_amount
                                          : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                    },
                                  },
                                });

                                if (val > total_remaining_amount)
                                  val = total_remaining_amount;
                                if (val < 0) val = 0;

                                setFieldsValue({
                                  partialData: {
                                    ...values?.partialData,
                                    [item?._id]: {
                                      givenAmount:
                                        val > total_remaining_amount
                                          ? total_remaining_amount
                                          : val,
                                      creditAmountType:
                                        values?.partialData?.[item?._id]
                                          ?.creditAmountType,
                                      receiveAmount: val,
                                      remainingAmount:
                                        total_remaining_amount - val,
                                    },
                                  },
                                });
                              }
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {item?.remainingRefundableAmount === 0 && (
                    <Badge type={"success"} label="refunded" />
                  )}
                </>
              ) : !item?.refund?.info?.updatedAt ? (
                <button
                  type="button"
                  onClick={() => {
                    if (values?.partialData?.[item?._id]) {
                      const partialData = {
                        ...values?.partialData,
                      };
                      delete partialData?.[item?._id];
                      setFieldsValue({ partialData: partialData });
                    } else {
                      setFieldsValue({
                        partialData: {
                          ...values?.partialData,
                          [item?._id]: {
                            creditAmountType: "full",
                            receiveAmount: 0,
                            remainingAmount: 0,
                          },
                        },
                      });
                    }
                  }}
                  className="text-red-500 font-medium text-sm hover:text-red-500/60 flex items-center gap-2"
                >
                  <Checkbox checked={!!values?.partialData?.[item?._id]} />
                  Cancel free ticket
                </button>
              ) : (
                <div className="text-sm font-medium text-red-500">
                  Cancelled
                </div>
              )}
            </div>
          ),
        },
        {
          _id: "remaining",
          type: "text",
          data: (
            <div>
              $
              {parseFloat(
                (item?.remainingRefundableAmount || 0) -
                  (item_value?.receiveAmount || 0)
              ).toFixed(2)}
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
      ],
    };
  });

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Update Order Status"
      size="xl2"
      titleIcon="fa-regular fa-clipboard-list"
      titleClass="!font-semibold !text-xl"
      headerClass="!text-black border-b border-gray-200 bg-gray-50 py-4 px-6"
      childrenClasses="!bg-white"
      bodyClasses="!py-6 !px-6"
    >
      {isLoading ? (
        <Skeleton width={600} height={600} />
      ) : (
        <div className="space-y-6">
          {/* Order Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Order ID:
                </span>
                <p className="text-gray-900 font-semibold">
                  {orderData?.data?.orderId}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Total Amount:
                </span>
                <p className="text-gray-900 font-semibold">
                  ${parseFloat(orderData?.data?.total || 0).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Customer:
                </span>
                <p className="text-gray-900 text-base font-medium">
                  {`${orderData?.data?.userData?.firstName || ""} ${
                    orderData?.data?.userData?.lastName || ""
                  }`.trim() || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Order Date:
                </span>
                <p className="text-gray-900 text-base font-medium">
                  {dayjs(orderData?.data?.date).format("MMM DD, YYYY")}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Event:
                </span>
                <p className="text-gray-900 text-base font-medium">
                  {lngDetect(orderData?.data?.eventData?.title) || "N/A"}
                </p>
              </div>
              {(selectedStatus === "canceled" ||
                selectedStatus === "partial-refund") &&
                !openPayment && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Refund Status:
                    </span>
                    <p className="text-gray-900 text-base font-medium capitalize">
                      {orderData?.data?.refund[0]?.status || "N/A"}
                    </p>
                  </div>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              {(selectedStatus === "canceled" ||
                selectedStatus === "partial-refund") &&
                !openPayment && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Refund Amount:
                    </span>
                    <p className="text-gray-900 text-base font-medium">
                      {"$" + orderData?.data?.refund[0]?.refundAmount || "N/A"}
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* Warning Message */}
          {warningMessage && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="fa-solid fa-triangle-exclamation text-amber-500 text-lg"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-amber-800">
                    Status Change Restriction
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    {warningMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Status */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">
              Current Status:
            </span>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: currentStatusStyle.backgroundColor,
                color: currentStatusStyle.color,
                borderColor: currentStatusStyle.borderColor,
              }}
            >
              {orderData?.data?.status?.info?.charAt(0)?.toUpperCase() +
                orderData?.data?.status?.info?.slice(1)}
            </span>
          </div>

          {/* Status Selection */}
          {!isStatusFinal ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                New Status <span className="text-red-500">*</span>
              </label>
              {allowedOptions.length > 0 ? (
                <Select
                  placeholder="Select order status"
                  dropdownData={allowedOptions}
                  selectedValue={selectedStatus}
                  onChange={(value) => {
                    setSelectedStatus(value);
                    setOpenPayment(
                      value === "canceled" || value === "partial-refund"
                    );
                  }}
                  dropdownButtonClass="!h-12"
                />
              ) : (
                <div className="p-3 bg-gray-100 rounded-md border">
                  <p className="text-sm text-gray-600">
                    No status changes available for this order.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fa-solid fa-lock text-red-500"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    Status Locked
                  </p>
                  <p className="text-xs text-red-700">
                    This order has reached a final status and cannot be
                    modified.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation/Refund Options - Show when canceled status is selected */}
          {selectedStatus === "canceled" &&
            openPayment &&
            orderData?.data?.status?.info === "confirmed" && (
              <div className="space-y-6 col-span-8 mb-6 border-t border-gray-200 pt-6">
                <div className="text-lg font-semibold text-red-600">
                  <i className="fa-solid fa-exclamation-triangle mr-2"></i>
                  Cancellation & Refund Options
                </div>

                <Form handleSubmit={handleFormSubmit} onSubmit={() => {}}>
                  <div className="text-lg font-semibold">Choose an option</div>
                  <div className="flex gap-6">
                    {cancelOptions?.map((opt, i) => (
                      <div
                        key={i}
                        className={classNames(
                          "flex gap-2 bg-slate-100/50 rounded-md p-3",
                          opt?.disabled ? "opacity-50 cursor-not-allowed" : ""
                        )}
                      >
                        <Radio
                          value={opt?.value}
                          checked={values?.refundType === opt?.value}
                          radioName={"refundType"}
                          onChange={(e) => {
                            const update_value = { ...(values || {}) };
                            if (e.target.value === "full") {
                              validation = initial_validation;
                              delete update_value?.partialData;
                            } else {
                              validation = {};
                            }
                            setFieldsValue({
                              ...update_value,
                              [e.target.name]: e.target.value,
                            });
                          }}
                          disabled={opt?.disabled}
                        />
                        <div className="space-y-1">
                          <div className="font-semibold text-base">
                            {opt?.heading}
                          </div>
                          <div className="text-xs font-medium text-surface-05">
                            {opt?.subHeading}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Errors {...formValidate(errors, "refundType")} />
                  {values?.refundType !== "partial" && (
                    <Input
                      label={
                        "Cancellation reason (this will be sent to the buyer)"
                      }
                      inputName={"cancelReason"}
                      onChange={handleChange}
                      value={values?.cancelReason}
                      inputPlaceholder={"Enter reason"}
                      inputClasses={
                        "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                      }
                      {...formValidate(errors, "cancelReason")}
                    />
                  )}
                  <div
                    className={classNames(
                      "space-y-3",
                      orderData?.data?.status?.info === "processing"
                        ? "opacity-50"
                        : ""
                    )}
                  >
                    {values?.refundType === "partial" && (
                      <Table
                        tableHeadData={tableHeadData}
                        tableData={tableData || []}
                        pageCount={1}
                        currentPage={1}
                        isLoder={false}
                        isOrderingDisabled={true}
                      />
                    )}
                    <div
                      className={
                        values?.refundType !== "partial"
                          ? "grid grid-cols-2 gap-6"
                          : ""
                      }
                    >
                      {values?.refundType !== "partial" && (
                        <Select
                          label={"Refund or credit amount"}
                          placeholder={"Select Option"}
                          dropdownData={[
                            {
                              name: "Full amount",
                              value: "full",
                            },
                            {
                              name: "A percentage of amount",
                              value: "percentage",
                            },
                            {
                              name: "A specific amount",
                              value: "specific",
                            },
                            {
                              name: "No refund or credit",
                              value: "noRefund",
                            },
                          ]}
                          selectedValue={values?.creditAmountType}
                          onChange={(val) => {
                            setRemaining(0);
                            setReceiveAmount(0);
                            setPercentageAmount(0);
                            handleChange({
                              target: { name: "creditAmountType", value: val },
                            });
                          }}
                          isDisabled={
                            orderData?.data?.status?.info === "processing"
                          }
                          dropdownButtonClass={
                            "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple !rounded-md"
                          }
                          {...formValidate(errors, "creditAmountType")}
                        />
                      )}
                      <div>
                        <div className="text-xs font-medium text-text-disabled mb-1">
                          Refund or credit method
                        </div>
                        <Image
                          src={
                            orderData?.data?.payment?.gateway === "stripe"
                              ? stripe
                              : orderData?.data?.payment?.gateway ===
                                  "paypal" && paypal
                          }
                          classes={"w-24 h-12 object-contain"}
                        />
                      </div>
                    </div>
                    {values?.refundType !== "partial" && (
                      <>
                        {values?.creditAmountType === "full" && (
                          <Checkbox
                            checkboxLabel={
                              <span className="font-medium">
                                Keep transaction fee ($
                                {orderData?.data?.organizationTransactionFee ||
                                  0}
                                )
                              </span>
                            }
                            checked={values?.keepTransactionFee}
                            checkboxName={"keepTransactionFee"}
                            value={true}
                            onChange={handleChange}
                            isDisabled={
                              orderData?.data?.status?.info === "processing"
                            }
                          />
                        )}
                        {values?.creditAmountType === "percentage" && (
                          <div className="w-40 flex items-center gap-1">
                            <Input
                              inputType="number"
                              value={percentageAmount}
                              inputPlaceholder="percentage"
                              onChange={(e) => {
                                let val = Number(e.target.value || 0);

                                if (!isNaN(val)) {
                                  setPercentageAmount(val > 100 ? 100 : val);
                                  if (val > 100) val = 100;
                                  if (val < 0) val = 0;

                                  const total_remaining_amount =
                                    orderData?.data?.total || 0;

                                  // Calculate percentage of total
                                  const amount =
                                    (total_remaining_amount * val) / 100;
                                  setReceiveAmount(amount);
                                  setRemaining(total_remaining_amount - amount);
                                }
                              }}
                            />
                            <span>%</span>
                          </div>
                        )}

                        {values?.creditAmountType === "specific" && (
                          <div className="w-40 flex items-center gap-1">
                            <span>$</span>
                            <Input
                              inputType="number"
                              value={percentageAmount}
                              inputPlaceholder="amount"
                              onChange={(e) => {
                                let val = Number(e.target.value || 0);
                                if (!isNaN(val)) {
                                  const total_remaining_amount =
                                    orderData?.data?.total || 0;
                                  setPercentageAmount(
                                    val > total_remaining_amount
                                      ? total_remaining_amount
                                      : val
                                  );

                                  if (val > total_remaining_amount)
                                    val = total_remaining_amount;
                                  if (val < 0) val = 0;

                                  setReceiveAmount(val);
                                  setRemaining(total_remaining_amount - val);
                                }
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="space-y-6 p-5 bg-slate-100 rounded-xl">
                    <div className="text-lg mb-6 font-semibold">Summary</div>
                    <div className="grid grid-cols-2 gap-5">
                      <div className="space-y-1 w-full">
                        <div className="font-semibold text-base">
                          Remaining order value
                        </div>
                        <div className="font-semibold text-2xl">
                          $
                          {parseFloat(
                            values?.refundType === "full"
                              ? remaining
                              : values?.refundType === "partial"
                              ? Object.values(
                                  values?.partialData || {}
                                )?.reduce(
                                  (acc, cur) =>
                                    acc + (cur?.remainingAmount || 0),
                                  0
                                )
                              : 0
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="space-y-1 w-full">
                        <div className="font-semibold text-base">
                          Customer will receive
                        </div>
                        <div className="font-semibold text-2xl">
                          $
                          {parseFloat(
                            values?.refundType === "full"
                              ? receiveAmount
                              : values?.refundType === "partial"
                              ? Object.values(
                                  values?.partialData || {}
                                )?.reduce(
                                  (acc, cur) => acc + (cur?.receiveAmount || 0),
                                  0
                                )
                              : 0
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            )}

          {/* Status Flow Information */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              <i className="fa-solid fa-info-circle mr-2"></i>
              Order Status Flow
            </h4>
            <div className="text-xs text-blue-700 space-y-1">
              <p>
                <strong>Cancellation:</strong> Any status → Cancel Request →
                Canceled
              </p>
              <p>
                <strong>Note:</strong> Confirmed orders and beyond cannot revert
                to Processing.
              </p>
            </div>
          </div>

          {/* Single Update Status Button */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              buttonLabel="Cancel"
              variant="secondary"
              buttonFunction={handleClose}
              isDisable={isUpdating}
            />
            {!isStatusFinal && allowedOptions.length > 0 && (
              <Button
                buttonLabel="Update Status"
                variant="primary"
                buttonFunction={handleSubmit}
                isDisable={isUpdating || selectedStatus === currentStatus}
                {...(isUpdating && {
                  loading: true,
                  buttonIconPosition: "left",
                })}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

const Order = () => {
  const { t } = useTranslation();
  const { organizations, selectedOrganization } = useOrganization();
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const user_role = selectedOrganizationDetails?.data?.verificationData?.role;
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    data: null,
  });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [eventkeyword, seteventKeyword] = useState("");
  const eventsearch = useDebounce(eventkeyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [eventfilters, setEventFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["order", filters, search, selectedOrganization],
    queryFn: async () => {
      const res = await orderList({
        organization: selectedOrganization,
        ...filters,
        ...(search && { keyword: search }),
        ...(user_role === "promoter"
          ? {
              viewAs: "promoter",
            }
          : {
              viewAs: "seller",
            }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!selectedOrganization,
  });

  const { data: eventData } = useQuery({
    queryKey: ["Event", eventfilters, eventsearch, selectedOrganization],
    queryFn: async () => {
      const res = await eventList({
        organization: selectedOrganization,
        ...eventfilters,
        ...(eventsearch && { keyword: eventsearch }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!selectedOrganization,
  });

  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Total Amount"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
      ...tableFilter({
        filterKey: "event",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterSearch: seteventKeyword,
        isFilterSearch: true,
        filterList: eventData?.data?.map((item) => ({
          name: lngDetect(item?.title),
          value: item?._id,
          type: "checkbox",
        })),
      }),
    },
    {
      _id: 3,
      name: t("Order by"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Order date"),
      align: "left",
    },
    {
      _id: "paymentStatus",
      name: t("Payment Status"),
      align: "left",
      ...tableFilter({
        filterKey: "paymentStatus",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...paymentStatusList],
      }),
    },
    {
      _id: 5,
      name: t("Order Status"),
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...statusList],
      }),
    },
    {
      _id: 8,
      name: t("Action"),
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    const statusText =
      item?.status?.info?.charAt(0)?.toUpperCase() +
        item?.status?.info?.slice(1) || "-";
    const statusStyle = getStatusStyle(statusText);

    // Payment status styling
    const paymentStatus = item?.payment?.status || "pending";
    const paymentStatusText =
      paymentStatus.charAt(0)?.toUpperCase() + paymentStatus.slice(1);
    const paymentStatusStyle = getPaymentStatusStyle(paymentStatus);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.total
            ? `${item?.eventData?.currency?.symbol || "$"} ${parseFloat(
                item?.total
              ).toFixed(2)}`
            : "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.userData?.firstName || item?.userData?.lastName
              ? `${item?.userData?.firstName ?? ""} ${
                  item?.userData?.lastName ?? ""
                }`
              : "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        },
        {
          _id: "paymentStatus",
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: paymentStatusStyle.backgroundColor,
                color: paymentStatusStyle.color,
                borderColor: paymentStatusStyle.borderColor,
              }}
            >
              {paymentStatusText}
            </span>
          ),
        },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <button
              onClick={() => setStatusModal({ isOpen: true, data: item })}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 hover:shadow-md hover:opacity-80 cursor-pointer"
              style={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                borderColor: statusStyle.borderColor,
              }}
              title="Click to change status"
            >
              {statusText}
            </button>
          ),
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "details",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function orderManageResponse({ res, status }) {
    if (res?.status === 200 || res?.status === 201) {
      refetch();
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // Status update success handler
  function statusUpdateResponse(res) {
    if (res?.status === 200) {
      refetch();
      setStatusModal({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || "Failed to update status");
    }
  }

  //   Order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await orderDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.filter(
              (item) => !payload?.includes(item?._id)
            );

            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["order", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await orderOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              {t("Order Management")}
            </div>
            <div className="text-sm xl:text-base h-10">
              {t(
                "Easily handle all order actions: update order info, status, and add new order data."
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="relative">
            <Search
              placeholder={t("Search here")}
              divClasses={"!border-slate-300 !bg-slate-50 !mb-0"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} {t("Selected")}
                <button
                  onClick={() => setSelectedItems([])}
                  type="button"
                  className="cursor-pointer"
                >
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(["order", filters, search], (state) => {
              if (state) {
                return { ...state, data: val };
              }
            });
          }}
          handleOrdering={(newData, oldData) =>
            handleOrdering({ newData, oldData })
          }
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>

      {/* Status Update Modal */}
      <OrderStatusUpdateModal
        isOpen={statusModal?.isOpen}
        orderItemData={statusModal?.data}
        onClose={() => setStatusModal({ isOpen: false, data: null })}
        onSuccess={statusUpdateResponse}
      />

      {/* Existing Modals */}
      <ManageOrderDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={orderManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Order;
